import { React, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Permissions from '../auth/Permissions';
import { updateStatus } from './state/forms.actions';
import { toTitleCase } from '../helpers/helpers';
import OntraccrTag from '../common/OntraccrTag';
import colors from '../constants/Colors';

/**
 * Generates/styles toggleable status tag (need manage forms permission to edit)
 * @param {string} status
 * @returns antd Tag component with select dropdown onClick
 */
export default function FormStatusTags({ status, record }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const formId = record?.id;
  const statuses = useSelector((state) => state.forms.statuses || {});
  const companyStatuses = useMemo(() => Object.values(statuses), [statuses]);
  const dispatch = useDispatch();
  const canEdit = Permissions.has('FORMS_WRITE');
  // Get current status from statuses map using statusId
  const currentStatus = statuses[record?.statusId];
  // so tag updates along with state
  const displayStatus = currentStatus?.status || status;
  const handleStatusChange = async (newStatusId) => {
    setIsUpdating(true);
    try {
      const selectedStatus = statuses[newStatusId];
      await dispatch(updateStatus(formId, selectedStatus?.id));
    } finally {
      setIsUpdating(false);
      setIsEditing(false);
    }
  };

  const renderStatusSelectTag = (statusColor) => (
    <Select
      size="small"
      style={{
        color: statusColor,
      }}
      placeholder="Change status"
      value={record?.statusId}
      onChange={handleStatusChange}
      onClick={(e) => e.stopPropagation()}
      optionLabelProp="label"
      autoFocus
      getPopupContainer={() => document.body}
      dropdownAlign={{ offset: [0, 10] }}
      dropdownStyle={{ minWidth: '180px' }}
      dropdownMatchSelectWidth={false}
    >
      {companyStatuses?.map((statusOption) => (
        <Select.Option
          key={statusOption.id}
          value={statusOption.id}
          label={toTitleCase(statusOption.status)}
        >
          {toTitleCase(statusOption.status)}
        </Select.Option>
      ))}
    </Select>
  );

  const renderStatusTag = ({
    color, textStyle, style, needsBorder,
  } = {}) => (
    <OntraccrTag
      color={color}
      style={style}
      textStyle={textStyle}
      needsBorder={needsBorder}
      className="form-status-tag"
      onClick={(e) => {
        if (canEdit) {
          e.stopPropagation();
          setIsEditing(true);
        }
      }}
    >
      {toTitleCase(displayStatus)}
    </OntraccrTag>
  );

  if (isUpdating) {
    return (
      <OntraccrTag
        color="gray"
        className="form-status-tag"
        style={{ opacity: 0.7 }}
      >
        <Spin size="small" style={{ marginRight: 4 }} />
        Updating...
      </OntraccrTag>
    );
  }
  if (isEditing && canEdit) {
    switch (displayStatus) {
      case 'rejected':
        return renderStatusSelectTag('red');
      case 'completed':
        return renderStatusSelectTag('darkgreen');
      case 'submitted':
      case 'initiated':
      default:
        return renderStatusSelectTag('black');
    }
  }
  switch (displayStatus) {
    case 'submitted':
      return renderStatusTag(
        {
          color: colors.ONTRACCR_DARK_YELLOW,
          textStyle: { color: 'black' },
          style: { color: 'black' },
        },
      );
    case 'rejected':
      return renderStatusTag(
        {
          color: 'red',
          textStyle: { color: '#f5222d' },
          style: { border: '1px solid #ffa39e' },
        },
      );
    case 'completed':
      return renderStatusTag(
        {
          color: 'darkgreen',
          style: { cursor: 'pointer' },
        },
      );
    case 'initiated':
    default:
      return renderStatusTag(
        {
          needsBorder: true,
        },
      );
  }
}

FormStatusTags.propTypes = {
  status: PropTypes.string,
  record: PropTypes.shape({
    id: PropTypes.string,
    statusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  canEdit: PropTypes.bool,
};
FormStatusTags.defaultProps = {
  status: '',
  record: {},
  canEdit: false,
};
